/** @author PVCOMBANK */
import * as React from "react";
import { Button, Checkbox, Divider, Paragraph, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { StyleSheet } from "react-native";
import { QueueSpacing, StackSpacing } from "app/shared/components/spacing";
import { testProps } from "app/shared/util/test-props";
import { InputText, InputTextMultiline } from "app/shared/components/form/input";
import { regexEmail, regexValidName, replaceEnterKey, replaceSpace, ValidatePhoneField } from "app/shared/util/validate";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import { CARD_TYPE_REGISTER } from "../../card-utils/card-utils";
import { ParamCardPhysicalRegister } from "../../reducer/types";
import { getCardStoreSelector, getStatusCardPhysicalRegisterSelector } from "../../reducer/selectors";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "app/modules/modal-management/reducer/action";
import { setCardStore } from "../../reducer/actions";
import { getUserProfileSelector } from "app/modules/account-management/reducer/selectors";

export interface FormCardRegisterProps {
  requestRegister: Function;
  onCancel: Function;
}

enum FormEnum {
  fullName,
  phoneNum,
  email,
  address,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormCardRegisterData = {
  fullName: string;
  phoneNum: string;
  email: string;
  address: string;
};

const FormCardRegister = (props: FormCardRegisterProps) => {
  const { requestRegister, onCancel } = props;
  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();
  const dispatchStore = useDispatch();

  // Instance Form Method
  const { control, handleSubmit, formState, reset, getValues, setValue } = useForm<FormCardRegisterData>({
    mode: "onChange",
  });

  const cardStore = useSelector(getCardStoreSelector);
  const statusRegisterStore = useSelector(getStatusCardPhysicalRegisterSelector);
  const userInfo = useSelector(getUserProfileSelector);

  const [cardTypeArray, setCardTypeArray] = React.useState<ItemCompleteProps[]>([]);
  const [radioAgree, setRadioAgree] = React.useState<boolean>(false);
  const [cardType, setCardType] = React.useState<string>("");
  const [disable, setDisable] = React.useState<boolean>(true);

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    fullName: {
      required: {
        value: true,
        message: t("CARD_THONG_BAO_HO_VA_TEN_TRONG"),
      },
      validate: {
        regexName: (value: string) => {
          const regex = regexValidName();
          if (regex.test(value)) {
            return t("CARD_THONG_BAO_HO_VA_TEN_KHONG_KI_TU_DAC_BIET").toString();
          }
          return true;
        },
        spaceName: (value: string) => {
          if (replaceEnterKey(replaceSpace(value)) === "") {
            return t("CARD_THONG_BAO_HO_VA_TEN_TRONG").toString();
          }
          return true;
        },
      },
    },
    phoneNum: {
      required: {
        value: true,
        message: t("CARD_THONG_BAO_SO_DIEN_THOAI_TRONG"),
      },
      validate: {
        phone: (value) => {
          if (!ValidatePhoneField(value)) {
            return t("CARD_THONG_BAO_SO_DIEN_THOAI_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    email: {
      required: {
        value: true,
        message: t("CARD_THONG_BAO_EMAIL_TRONG"),
      },
      validate: {
        regex: (value: string) => {
          const regex = regexEmail();
          if (!regex.test(value)) {
            return t("CARD_THONG_BAO_EMAIL_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    address: {
      required: {
        value: false,
        message: "",
      },
    },
  };

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textNote: { ...fonts.regular, color: colors.text, textAlign: "justify" },
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
        textAgree: { ...fonts.regular, color: colors.text, marginLeft: spacing.medium, textAlign: "justify" },
      }),
    [colors, fonts],
  );

  const handleRequest = (value: FormCardRegisterData): void => {
    const data: ParamCardPhysicalRegister = {
      fullName: replaceEnterKey(replaceSpace(value.fullName)),
      phoneNum: replaceEnterKey(replaceSpace(value.phoneNum)),
      email: replaceEnterKey(replaceSpace(value.email)),
      cardType: cardType,
      officeAddress: replaceEnterKey(replaceSpace(value.address)),
    };
    requestRegister(data);
  };

  const onPressCreate = () => {
    handleSubmit(handleRequest).call(() => {});
  };

  const onClickChecked = (value: number) => {
    if (cardTypeArray.length) {
      const arrayNew: ItemCompleteProps[] = cardTypeArray.map((_item, index) => {
        if (index === value) {
          _item.other = _item.other === "1" ? "0" : "1";
        }
        return _item;
      });
      console.log("arrayNew: ", arrayNew);
      setCardTypeArray(arrayNew);
    }
  };

  /// MARK: - Child Component

  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("CARD_THONG_TIN_DANG_KY")}</Paragraph>
      <Paragraph style={styles.textNote}>{t("CARD_GIOI_THIEU_CHUC_NANG_PHAT_HANH_THE_VAT_LY")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormCardType = cardTypeArray.length ? (
    <>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("CARD_CHON_LOAI_THE")}</Paragraph>
      <StackSpacing size="medium" />
      {cardTypeArray.map((_item, index) => {
        return (
          <Block key={index} row center>
            <Checkbox status={_item?.other === "1" ? "checked" : "unchecked"} onPress={() => onClickChecked(index)} />
            <Paragraph style={styles.textAgree}>{_item.label}</Paragraph>
          </Block>
        );
      })}
      <StackSpacing size="medium" />
      <Divider />
      <StackSpacing size="medium" />
    </>
  ) : (
    <></>
  );

  const FormAgree = (
    <Block row>
      <Checkbox status={radioAgree ? "checked" : "unchecked"} onPress={() => setRadioAgree(!radioAgree)} />
      <Paragraph style={styles.textAgree}>{t("CARD_DIEU_KHOAN_PHAT_HANH_THE_VAT_LY")}</Paragraph>
    </Block>
  );

  const FormInput = (
    <>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText
            name="fullName"
            control={control}
            rules={validateSchemeForm.fullName}
            inputProps={{}}
            label={t("CARD_HO_VA_TEN") + " *"}
          />
        </Block>
        <QueueSpacing size="huge" />
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText
            name="phoneNum"
            control={control}
            rules={validateSchemeForm.phoneNum}
            inputProps={{ keyboardType: "phone-pad" }}
            label={t("BENE_SO_DIEN_THOAI") + " *"}
          />
        </Block>
      </Block>
      <InputTextMultiline
        name="email"
        control={control}
        rules={validateSchemeForm.email}
        inputProps={{ keyboardType: "email-address", multiline: true }}
        label={t("CARD_EMAIL") + " *"}
      />
      <InputTextMultiline
        name="address"
        control={control}
        rules={validateSchemeForm.address}
        inputProps={{ multiline: true }}
        label={t("CARD_DIA_CHI")}
      />
      <StackSpacing size="medium" />
      {FormCardType}
      {FormAgree}
    </>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Button onPress={() => onCancel()} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} disabled={disable} onPress={onPressCreate} {...testProps("nextBtn")}>
        {t("CARD_DANG_KY")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    setCardTypeArray(CARD_TYPE_REGISTER);
  }, []);

  React.useEffect(() => {
    if (cardTypeArray.length) {
      let _cardType: string = "";
      cardTypeArray.forEach((_item) => {
        if (_item.other === "1") _cardType = _cardType + (_cardType === "" ? "" : ";") + _item.value;
        return _item;
      });
      setCardType(_cardType);
    }
  }, [cardTypeArray]);

  React.useEffect(() => {
    if (userInfo && !getValues("fullName").length) {
      setValue("fullName", userInfo?.FULLNAME || "");
    }
  }, [getValues, setValue, userInfo]);

  React.useEffect(() => {
    console.log("formState.isValid", formState.isValid);
    if (formState.isValid && radioAgree && cardType.length) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, [formState.isValid, radioAgree, cardType]);

  React.useEffect(() => {
    if (statusRegisterStore === "ok") {
      dispatchStore(setCardStore({ ...cardStore, resCardPhysicalRegister: undefined, statusPhysicalRegister: "init" }));
      dispatchStore(
        showModal({
          modalType: "alert",
          modalProps: {
            open: true,
            message: t("CARD_THONG_BAO_YEU_CAU_PHAT_HANH_THE"),
          },
        }),
      );
      reset();
      if (radioAgree) setRadioAgree(false);
    }
  }, [statusRegisterStore, radioAgree, dispatchStore, t, cardStore, reset]);

  return (
    <>
      <Block pt={spacing.medium}>
        <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
          {FieldForm}
          {FormInput}
        </Block>
        {ButtonForm}
      </Block>
    </>
  );
};

export default FormCardRegister;
